import React, { useMemo, useRef, useState } from 'react';
import { Dropdown, SearchButton, SwitcherTab, Table } from '@silverfort/sf-components';
import useGetColumns from './columns/useGetColumns';
import useGetRefSize from '../../../../hooks/useGetRefSize';
import useGetRiskEntities from '../../api/queries/risk-entities/useGetRiskEntities';
import { RISK_INDICATORS_OPTIONS, RISK_SEVERITIES_OPTIONS } from './filters/FilterOptions';
import { AD_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';

const ENTITY_TYPES_TABS = [
    {
        text: 'Users',
        value: AD_ENTITY_TYPES.USER
    },
    {
        text: 'Resources',
        value: AD_ENTITY_TYPES.SERVER
    }
];

const RiskEntitiesPopupContent = () => {
    const tableRef = useRef(null);
    const [entityName, setEntityName] = useState<string>();
    const [riskSeverity, setRiskSeverity] = useState<number[]>();
    const [entityType, setEntityType] = useState<number>(ENTITY_TYPES_TABS[0].value);
    const [riskIndicators, setRiskIndicators] = useState<string[]>();
    const [sortColumn, setSortColumn] = useState<string>('risk_level');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const { height: tableHeight } = useGetRefSize(tableRef);

    const {
        data: pagable,
        isFetching,
        fetchNextPage
    } = useGetRiskEntities({
        entityName,
        riskSeverity,
        entityType,
        riskIndicators,
        orderByField: sortColumn,
        orderByDirection: sortDirection
    });
    const columns = useGetColumns();
    const rows = useMemo(() => pagable?.pages.flat() ?? [], [pagable?.pages.length]);

    const handleSortChange = (columnKey: string, direction: 'asc' | 'desc') => {
        setSortColumn(columnKey);
        setSortDirection(direction);
    };

    return (
        <div className="h-full w-full flex flex-col flex-1">
            <div className="w-full h-14 flex items-center justify-between flex-none">
                <div className="h-full flex items-center gap-2">
                    <SwitcherTab
                        items={ENTITY_TYPES_TABS}
                        onSelect={setEntityType}
                        selectedItem={entityType}
                        itemClassName="flex-none w-fit"
                    />
                    <Dropdown
                        variant="filter"
                        title="Risk level"
                        items={RISK_SEVERITIES_OPTIONS}
                        selectedItems={riskSeverity}
                        onChange={(newSeverities) => setRiskSeverity(newSeverities as number[])}
                        onRemove={() => setRiskSeverity([])}
                        removable={!!riskSeverity?.length}
                        multiple
                    />
                    <Dropdown
                        variant="filter"
                        title="Risk indicators"
                        items={RISK_INDICATORS_OPTIONS}
                        selectedItems={riskIndicators}
                        onChange={(newIndicators) => setRiskIndicators(newIndicators as string[])}
                        onRemove={() => setRiskIndicators([])}
                        removable={!!riskIndicators?.length}
                        multiple
                        searchable
                    />
                </div>
                <SearchButton setSearchString={setEntityName} searchString={entityName} iconOnly />
            </div>
            <div className="h-full w-full" ref={tableRef}>
                <Table
                    columns={columns}
                    rows={rows}
                    onScrollEnd={fetchNextPage}
                    loading={isFetching}
                    bodyHeight={(tableHeight ?? 0) - 56}
                    tableContainerClassName="w-full h-full"
                    EmptyStateProps={{ title: 'No data', className: 'w-full max-h-[90%]' }}
                    onSortChange={handleSortChange}
                />
            </div>
        </div>
    );
};

export default RiskEntitiesPopupContent;
